<template>
  <div class="container-fluid page-container">
    <div v-if="!canPayByCard" class="row">
      <div class="col-12">
        <b-card class="shadow border overflow-hidden mt-5">
          <b-card-body class="text-center p-5">
            <h2 class="h3 mb-4">{{ $t('Access Denied') }}</h2>
            <p class="h5 font-weight-normal">{{ $t('You do not have permission to access payment methods') }}.</p>
            <router-link :to="{ name: 'dashboard' }" class="btn btn-primary mt-4">
              {{ $t('Return to Dashboard') }}
            </router-link>
          </b-card-body>
        </b-card>
      </div>
    </div>
    <div v-else>
      <div class="row page-header__container mb-4 pb-3">
        <page-heading :class="'col align-items-center'" :title="pageTitle" />
      </div>
      <div class="row">
      <div class="col" :class="[{'col-xl-7 col-md-6': showNewPaymentMethodForm}]">
        <div class="row">
          <div class="col-12 payment-control pb-3">
            <div v-if="canAutoPay">
              <b-card
                no-body
                class="bg-white overflow-hidden autopay border p-3"
              >
                <div class="row">
                  <div class="col-12 autopay__heading-sm">
                    <h2 class="font-size-14 font-weight-bold">
                      {{ $t('Automatic payment by card') }}
                      <font-awesome-icon
                        id="automatic-payment-tooltip-sm"
                        icon="info-circle"
                      />
                    </h2>
                    <b-tooltip target="automatic-payment-tooltip-sm" triggers="hover">
                      {{ $t('We will automatically charge invoices to your card as soon as they are due') }}.
                    </b-tooltip>
                  </div>
                  <div class="col-7 col-md-7 col-lg-8 col-sm-9 autopay__description">
                    <h2 class="font-size-14 font-weight-bold autopay__heading">
                      {{ $t('Automatic payment by card') }}
                      <font-awesome-icon
                        id="automatic-payment-tooltip"
                        icon="info-circle"
                      />
                    </h2>
                    <b-tooltip target="automatic-payment-tooltip" triggers="hover">
                      {{ $t('We will automatically charge invoices to your card as soon as they are due') }}.
                    </b-tooltip>
                    <p class="h5 font-weight-normal">
                      {{ $t('Would you like us to take payment by card automatically?') }}
                    </p>
                  </div>
                  <div class="col-5 col-md-5 col-lg-4 col-sm-3 autopay__toggle d-flex align-items-center justify-content-end">
                    <b-checkbox
                      v-model="automaticPaymentsEnabled"
                      class="custom-switch-text"
                      switch
                      @change="toggleAutoPay"
                    >
                      <span class="hide">{{ $t('Automatic') }}</span><span class="show">{{ $t('Manual') }}</span>
                    </b-checkbox>
                  </div>
                </div>
              </b-card>

              <div class="py-3">
                <hr class="w-100">
              </div>
            </div>

            <spinner v-if="busy" />
            <div v-else>
              <b-card v-for="card in paymentMethods" :key="card.id" class="shadow border overflow-hidden payment-card-container mb-3">
                <b-card-header v-if="willCheckDefaultCard(card.default)" class="card-header__default">
                  <h2 class="font-size-14 m-0">
                    {{ $t('This is your default card') }}
                  </h2>
                </b-card-header>
                <b-card-body class="p-2 p-sm-3 row m-0 justify-content-center align-items-center flex-row">
                  <div class="col card-details">
                    <span class="h5 pb-0 mb-0 font-weight-500">{{ card.cardnum }}</span>
                    <br>
                    <span class="small">{{ $t('Expiry Date', {expirydate: card.expirydate}) }}</span>
                  </div>
                  <div class="mt-2 card-actions mt-md-0 align-items-baseline d-flex text-md-right">
                    <b-button size="sm" variant="outline-secondary" :disabled="!canRemoveCard(card)" @click="removePaymentMethod(card)">
                      <font-awesome-icon
                        class="mr-2 text-danger"
                        :icon="['fas', 'minus-circle']"
                      />
                      <span :id="'btn-remove-card-' + card.id" v-b-tooltip.hover>
                        {{ $t('Remove') }}
                      </span>
                      <b-tooltip v-if="!canRemoveCard(card)" :target="'btn-remove-card-' + card.id">
                        {{$t('Cannot remove this card')}}
                        <router-link :to="{ path: '/help/contact' }">{{ $t('here') }}</router-link>
                      </b-tooltip>
                    </b-button>
                    <b-button size="sm" variant="secondary" :disabled="willCheckDefaultCard(card.default)" class="ml-2" @click="setAsDefaultCard(card.id)">
                      <span
                        v-b-tooltip.hover
                        class="d-flex justify-content-center w-100 stretched-link"
                        :title="willCheckDefaultCard(card.default) ? $t('This card is already your default card'): ''"
                      >{{ $t('Make Default') }}</span>
                    </b-button>
                  </div>
                </b-card-body>
              </b-card>
            </div>

            <div class="col-12 pt-3 d-flex justify-content-center">
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="onAddPaymentMethod"
              >
                <font-awesome-icon
                  class="mr-1"
                  :icon="['fas', 'credit-card']"
                />
                {{ $t('Add a new payment method') }}
                <font-awesome-icon
                  class="text-success ml-1"
                  :icon="['fas', 'plus-circle']"
                />
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <div ref="newPaymentContainer" class="col-12 col-md-6 col-xl-5 mt-3 mt-md-0">
        <b-card
          v-if="showNewPaymentMethodForm"
          class="rounded shadow overflow-hidden border bg-white"
        >
          <template v-slot:header>
            <div class="d-flex align-items-center">
              <b-button class="close position-absolute" variant="link" @click="showNewPaymentMethodForm = false">
                <font-awesome-icon icon="times" />
              </b-button>
              <h2 class="text-center m-auto h4">
                {{ $t('Add a new payment method') }}
              </h2>
            </div>
          </template>
          <new-payment-method :is-payment-method-page="true" :is-card-setup="true" class="p-3" @loaded="onAddPaymentMethodLoaded" @created="paymentMethodCreated()" />
        </b-card>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import NewPaymentMethod from '@/components/form/NewPaymentMethod';
import PageHeading from "@/components/control/PageHeading";
import notification from "@/imports/mixins/notification";
import companyAccount from "@/imports/mixins/companyAccount";
import Spinner from "@/components/animation/Spinner";

export default {
  name: "PaymentMethods",
  title: 'Payment Methods',
  components: {NewPaymentMethod, PageHeading, Spinner},
  mixins: [notification, companyAccount],
  data() {
    return {
      pageTitle: this.$t('Payment Methods'),
      paymentMethods: [],
      automaticPaymentsEnabled: null,
      busy: true,
      showNewPaymentMethodForm: false,
      canAutoPay: false,
    };
  },
  computed: {
    isChild() {
      return this.$router.currentRoute.name !== 'payment-methods'
    },
    validPaymentMethods() {
      return this.paymentMethods.filter(p => !p.expired)
    }
  },
  methods: {
    onAddPaymentMethod() {
      this.showNewPaymentMethodForm = true;
      this.$nextTick(() => this.$refs.newPaymentContainer.scrollIntoView());
    },
    onAddPaymentMethodLoaded() {
      this.$refs.newPaymentContainer.scrollIntoView();
    },
    loadPaymentMethods() {
      this.busy = true;
      this.$api.payments.getPaymentMethods().then(({data}) => {
        this.paymentMethods = data.data;
        this.automaticPaymentsEnabled = data.meta.autoPayEnabled;
        this.canAutoPay = data.meta.canAutoPay;
      })
        .finally(() => this.busy = false);
    },
    removePaymentMethod(card) {
      if (this.canRemoveCard(card)) {
        if (confirm(this.$t('Are you sure you want to remove this payment method?'))) {
          this.busy = true;
          this.$api.payments.delete(card.id).then(() => {
            this.loadPaymentMethods();
          });
        }
      }
    },
    paymentMethodCreated() {
      this.showNewPaymentMethodForm = false;
      this.loadPaymentMethods();
    },
    toggleAutoPay(newValue) {
      this.$api.payments.setAutoPay(newValue)
        .then(() => this.notify(
          this.$bvToast,
          this.$t('Automatic payment has been successfully', {enabledOrDisabled: newValue ? 'enabled' : 'disabled' }),
          null,
          'success'
        ))
        .catch(() => this.notify(this.$bvToast, this.$t('There was a problem', {enablingOrDisabling: newValue ? 'enabling' : 'disabling' }), null, 'danger'));
    },
    setAsDefaultCard(id) {
      this.$api.payments.setDefault(id);
      for (const card of this.paymentMethods) {
        card.default = card.id === id;
      }
    },
    willCheckDefaultCard: function(card) {
      return Number(card) === 1
    },
    canRemoveCard: function(card) {
      return card.expired || this.validPaymentMethods.length > 1
    }
  },
  created() {
    this.loadPaymentMethods();
  },
}
</script>

<style scoped lang="scss">
.card-header {
  color: $theme-primary;
  &__invalid {
    background: $red;
  }
}

.payment {
  &-card-container,
  &-control {
    max-width: 560px;
  }
}

.autopay {
  &__heading {
    display: block
  }
  &__heading-sm {
    display: none
  }
}

@media only screen and (max-width: 414px) {
  .autopay {
    &__heading {
      display: none
    }
    &__heading-sm {
      display: block
    }
  }
}

@media only screen and (min-width: 376px) and (max-width: 414px) {
  .autopay__description,
  .payment-card-container .card-details {
    padding-right: 0;
  }

  .payment-card-container {
    .card-details {
      max-width: 40%;
      padding-left: 0;
    }

    .card-actions {
      max-width: 60%;
    }
  }
}

@media only screen and (max-width: 375px) {
  .card-details {
    text-align: center;
  }
  .autopay__description {
    padding-right: 0;
  }
  .autopay__toggle {
    padding-left: 0;
  }
}

</style>
